import { Component, Inject } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { MotifModule } from '@ey-xd/ng-motif';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatDialogModule, MatDialogActions, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FeedbackCategoryService, FeedbackCategory } from '../../../_services/feedback-category.service';
import { MappingResultService } from '../../../_services/mapping-result.service';

@Component({
  selector: 'app-feedback-category-modal',
  standalone: true,
  imports: [
    CommonModule,
    MotifModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatDialogModule,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatButtonModule,
    MatInputModule,
    FormsModule
  ],
  templateUrl: './feedback-category-modal.component.html',
  styleUrls: ['./feedback-category-modal.component.scss']
})
export class FeedbackCategoryModalComponent {
  categories: FeedbackCategory[] = [];
  selectedCategory: string = '';
  commentText: string = '';
  loading = false;
  categoriesLoading = false;
  categoriesError: string | null = null;

  constructor(
    private dialogRef: MatDialogRef<FeedbackCategoryModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private feedbackCategoryService: FeedbackCategoryService,
    private mappingResultService: MappingResultService
  ) {
    this.loadCategories();
  }

  loadCategories() {
    this.categoriesLoading = true;
    this.categoriesError = null;
    this.feedbackCategoryService.getFeedbackCategoryList().subscribe({
      next: (cats) => {
        let filtered = (cats || [])
          .filter(c => c && c.category_name && c.category_name.trim().length > 0)
          .map(c => ({
            ...c,
            category_name: c.category_name.trim().toLowerCase() === 'other' ? 'Other' : c.category_name
          }));
        if (!filtered.map(c => c.category_name.toLowerCase()).includes('other')) {
          filtered.push({ category_id: 'other', category_name: 'Other', timestamp: '' });
        }
        this.categories = filtered;
        this.categoriesLoading = false;
      },
      error: () => {
        this.categoriesError = 'Failed to load categories';
        this.categoriesLoading = false;
      }
    });
  }

  submit() {
    this.loading = true;
    const trimmed = this.commentText ? this.commentText.trim() : '';
    let comment = '';
    const sel = (this.selectedCategory || '').toLowerCase();
    if (sel === 'other') {
      comment = trimmed ? `${trimmed}` : '';
    } else {
      comment = trimmed;
    }
    if (this.commentRequired() && !comment) {
      this.loading = false;
      return;
    }
    const payload = {
      doc_run_id: this.data.doc_run_id,
      data_element_name: this.data.data_element_name,
      updated_by: this.data.updated_by,
      category_name: this.selectedCategory,
      comments: comment ? [comment] : []
    };
    const categoryKey = `previous_feedback_category_${this.data.doc_run_id}_${this.data.request_id}_${this.data.data_element_name}`;
    localStorage.setItem(categoryKey, this.selectedCategory || '');
    if (comment && comment.trim().length > 0) {
      const feedbackKey = `previous_feedback_comment_${this.data.doc_run_id}_${this.data.request_id}_${this.data.data_element_name}`;
      localStorage.setItem(feedbackKey, comment);
    }
    this.mappingResultService.feedbackCategoryComment(payload).subscribe({
      next: () => {
        this.loading = false;
        this.dialogRef.close({ success: true, category: this.selectedCategory, comment });
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  close() {
    this.dialogRef.close(false);
  }

  selectCategory(categoryName: string) {
    this.selectedCategory = categoryName;
  }

  commentRequired(): boolean {
    const sel = (this.selectedCategory || '').toLowerCase();
    if (sel === 'other') return true;
    if (sel.includes('partially') && sel.includes('correct')) return true;
    if (this.categories && this.categories.length >= 3) {
      const third = (this.categories[2].category_name || '').toLowerCase();
      if (third.includes('partially') || third.includes('partially correct')) {
        const selectedMatchesThird = third === sel || (this.selectedCategory && this.selectedCategory === this.categories[2].category_name);
        if (selectedMatchesThird) return true;
      }
    }

    return false;
  }

  getCommentPlaceholder(): string {
    return this.commentRequired() ? 'Comment is required for this category...' : 'Optional comment (you may provide more detail)';
  }
}
